/**
 * Dashboard Page - Analytics and metrics overview
 *
 * Features:
 * - Bug status pie chart
 * - Metric summary cards with pipeline saturation
 * - Task and PR status breakdowns
 * - Real-time data from API
 */
import { useMemo } from 'react';
import { BugStatusPieChart, MetricCard, TasksPRStatus } from '@/components/dashboard';
import { ErrorMessage, LoadingState } from '@/components/common';
import type { MetricCardProps } from '@/components/dashboard';
import { useDashboardPage } from '../hooks/useDashboardPage';

export function DashboardPage() {
  const { query, isAdmin, summary, taskBreakdown, prBreakdown, bugStatus, monthlyHistory } = useDashboardPage();

  const totalTasks = summary?.totalTasks ?? taskBreakdown?.total ?? 0;
  const avgResolveMinutes = summary?.avgResolutionTimeMinutes ?? 0;
  const acceptedCount = taskBreakdown?.completed ?? summary?.tasksCompleted ?? 0;
  const failedCount = taskBreakdown?.failed ?? summary?.tasksFailed ?? 0;
  const totalPullRequests = summary?.totalPullRequests ?? prBreakdown?.total ?? 0;
  const rejectedPrCount = prBreakdown?.closed ?? summary?.prsClosed ?? 0;
  const acceptanceRate = totalTasks > 0 ? Math.round((acceptedCount / totalTasks) * 100) : 0;
  const failureRate = totalTasks > 0 ? Math.round((failedCount / totalTasks) * 100) : 0;
  const rejectedPrRate = totalPullRequests > 0 ? Math.round((rejectedPrCount / totalPullRequests) * 100) : 0;

  const SLA_MINUTES = 180;
  const usedSlaPercent = (avgResolveMinutes / SLA_MINUTES) * 100;
  const avgTimeScore = Math.max(0, 100 - Math.round(usedSlaPercent));

  const aiCostDollars = (summary?.totalAiCostCents ?? 0) / 100;
  const aiCostPerBug = totalTasks > 0 ? aiCostDollars / totalTasks : 0;
  const costTarget = 2;
  const aiCostScore =
    aiCostPerBug > 0 ? Math.max(0, Math.min(100, Math.round((costTarget / aiCostPerBug) * 100))) : 0;

  const metricCards: MetricCardProps[] = useMemo(() => {
    const primaryCards: MetricCardProps[] = [
      {
        label: 'Avg Time To Resolve',
        detail: 'Average completion duration',
        background: 'from-sky-100/70 via-white/50 to-white dark:from-sky-900/30 dark:via-gray-900/60 dark:to-gray-900/40',
        iconColor: 'text-sky-500',
        barGradient: 'from-sky-500 to-cyan-400',
        icon: (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3" />
          </svg>
        ),
        value: avgResolveMinutes,
        valueDisplay: `${avgResolveMinutes}m`,
        share: avgTimeScore,
        shareLabel: 'Efficiency',
        footerLabel: 'SLA Target',
        footerValue: '<= 180m',
      },
      {
        label: 'Acceptance Rate',
        detail: 'Completed out of total bugs',
        background: 'from-emerald-100/70 via-white/50 to-white dark:from-emerald-900/25 dark:via-gray-900/50 dark:to-gray-900/40',
        iconColor: 'text-emerald-500',
        barGradient: 'from-emerald-500 to-lime-400',
        icon: (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 13l4 4L19 7" />
          </svg>
        ),
        value: acceptanceRate,
        valueDisplay: `${acceptanceRate}%`,
        share: acceptanceRate,
        shareLabel: 'Rate',
        footerLabel: 'Resolved Bugs',
        footerValue: acceptedCount.toString(),
      },
    ];

    const roleCard: MetricCardProps = isAdmin
      ? {
          label: 'AI Cost',
          detail: `${totalTasks > 0 ? `$${aiCostPerBug.toFixed(2)}` : '$0.00'} per bug`,
          background: 'from-violet-100/70 via-white/50 to-white dark:from-violet-900/30 dark:via-gray-900/60 dark:to-gray-900/40',
          iconColor: 'text-violet-500',
          barGradient: 'from-violet-500 to-fuchsia-400',
          icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 9V7a5 5 0 00-10 0v2M5 9h14l-1 10H6L5 9z" />
            </svg>
          ),
          value: aiCostDollars,
          valueDisplay: `$${aiCostDollars.toFixed(2)}`,
          share: aiCostScore,
          shareLabel: 'Score',
          footerLabel: 'Total Spend',
          footerValue: `$${aiCostDollars.toFixed(2)}`,
        }
      : {
          label: 'Failure Rate',
          detail: 'Failed out of total bugs',
          background: 'from-amber-100/70 via-white/50 to-white dark:from-amber-900/25 dark:via-gray-900/50 dark:to-gray-900/40',
          iconColor: 'text-amber-500',
          barGradient: 'from-amber-500 to-orange-400',
          icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          value: failureRate,
          valueDisplay: `${failureRate}%`,
          share: failureRate,
          shareLabel: 'Rate',
          footerLabel: 'Failed Bugs',
          footerValue: failedCount.toString(),
        };

    return [
      ...primaryCards,
      roleCard,
      {
        label: 'Rejected PR Rate',
        detail: 'Closed out of total pull requests',
        background: 'from-rose-100/70 via-white/50 to-white dark:from-rose-900/25 dark:via-gray-900/50 dark:to-gray-900/40',
        iconColor: 'text-rose-500',
        barGradient: 'from-rose-500 to-orange-400',
        icon: (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        value: rejectedPrRate,
        valueDisplay: `${rejectedPrRate}%`,
        share: rejectedPrRate,
        shareLabel: 'Rate',
        footerLabel: 'Rejected PRs',
        footerValue: rejectedPrCount.toString(),
      },
    ];
  }, [
    acceptedCount,
    acceptanceRate,
    aiCostDollars,
    aiCostPerBug,
    aiCostScore,
    avgResolveMinutes,
    avgTimeScore,
    failedCount,
    failureRate,
    isAdmin,
    rejectedPrCount,
    rejectedPrRate,
    totalTasks,
  ]);

  if (query.hasError) {
    return <ErrorMessage message="Failed to load dashboard" />;
  }

  return (
    <div className="space-y-8">
      {query.isLoading ? (
        <LoadingState message="Loading dashboard..." isLoading={query.isLoading} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {metricCards.map((card) => (
                <MetricCard key={card.label} {...card} />
              ))}
            </div>
            <BugStatusPieChart {...bugStatus} />
          </div>

          <div className="mb-6">
            <h3 className="font-body-base mb-4 text-gray-900 dark:text-white">Tasks & PR Status</h3>
            <TasksPRStatus monthlyHistory={monthlyHistory} />
          </div>
        </>
      )}
    </div>
  );
}
